import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { http } from '../config';
import { toast } from 'react-toastify';
import {
  TListPasskeysGetResponse,
  IRemovePasskeyDeleteResponse,
  ISendOTPPostResponse,
} from '../../types';

interface IPasskey {
  passkeyRegisterOptions: () => Promise<{
    publicKey: PublicKeyCredentialCreationOptionsJSON;
  }>;
  verifyPasskeyRegister: (payload: {
    credential: any;
  }) => Promise<{ status: number; data: any }>;
  getPasskeyLoginOptions: (
    customerId: string
  ) => Promise<{ publicKey: PublicKeyCredentialRequestOptionsJSON }>;
  verifyPasskeyLogin: (payload: {
    customerId: string;
    credential: any;
  }) => Promise<ISendOTPPostResponse>;
  listPasskeys: () => Promise<TListPasskeysGetResponse>;
  removePasskey: (id: number) => Promise<IRemovePasskeyDeleteResponse>;
}

export const passkey: IPasskey = {
  passkeyRegisterOptions: () =>
    http.criticalGet(`/api/auth/passkeys/passkey_registration_options`),
  verifyPasskeyRegister: (payload) =>
    http.criticalPost(
      `/api/auth/passkeys/passkey_registration_verify`,
      payload.credential
    ),
  getPasskeyLoginOptions: (customerId) =>
    http.post(`/api/auth/passkeys/v1/passkey_authentication_options`, {
      customer_id: customerId,
    }),
  verifyPasskeyLogin: (payload) =>
    http.post(`/api/auth/passkeys/v1/passkey_authentication_verify`, {
      customer_id: payload.customerId,
      ...payload.credential,
    }),
  listPasskeys: () => http.criticalGet(`/api/auth/passkeys/list_passkeys`),
  removePasskey: (id) =>
    http.delete(`/api/auth/passkeys/delete_passkey?id=${id}`),
};

export const usePasskeyRegister = () => {
  return useMutation({
    mutationKey: ['auth-passkey-register'],
    mutationFn: passkey.passkeyRegisterOptions,
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

export const useVerifyPasskeyRegister = () => {
  const client = useQueryClient();
  return useMutation({
    mutationKey: ['auth-passkey-verify-register'],
    mutationFn: passkey.verifyPasskeyRegister,
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['passkey-list'] });
    },
    onError: (error) => {
      const message = (
        error as unknown as { response: { data: { message: string } } }
      ).response.data.message;
      toast.error(message);
    },
  });
};

export const useListPasskeys = (enabled:boolean = true) => {
  return useQuery({
    queryKey: ['passkey-list'],
    queryFn: passkey.listPasskeys,
    enabled
  });
};

export const useRemovePasskey = () => {
  const client = useQueryClient();
  return useMutation({
    mutationKey: ['passkey-remove'],
    mutationFn: passkey.removePasskey,
    onSuccess: (res) => {
      client.invalidateQueries({ queryKey: ['passkey-list'] }),
        toast.success(res.message);
    },
    onError: (err) => {
      const message = (
        err as unknown as { response: { data: { message: string } } }
      ).response.data.message;
      toast.error(message);
    },
  });
};
